import { Body2, formFieldVerticalGap, Header4, inlineTextIconMargin, media, modalPaddingExtraSmall, sectionalMargin, styled } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { CreateScheduleConfigComponent, ScheduleConfigComponentRef } from "@iventis/schedule-config";
import React, { useEffect, useRef, useState } from "react";
import { ScheduleConfigService } from "@iventis/schedule-config/src/api";
import { Button } from "@mui/material";
import { Schedule } from "@iventis/domain-model/model/schedule";
import { AxiosError } from "axios";
import { SUPPORT_EMAIL } from "@iventis/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getSchedule, updateSchedule } from "@iventis/schedule/src/schedule-api-functions";

const ScheduleConfigComponent = CreateScheduleConfigComponent<AxiosError>();

export const ProjectScheduleConfig: React.FC<{ title: string; description: string; onSaved?: (schedule: Schedule) => void; onSaving?: () => void }> = ({
    title,
    description,
    onSaved,
    onSaving,
}) => {
    const translate = useIventisTranslate();
    const [saving, setSaving] = useState<boolean>();
    const [isValid, setIsValidation] = useState<boolean>();

    useEffect(() => {
        if (saving) onSaving?.();
    }, [saving]);

    const configService: ScheduleConfigService = {
        getConfiguration: getSchedule,
        updateConfiguration: (schedule: Schedule) => updateSchedule(schedule).then((d) => d.data),
    };

    const configComponent = useRef<ScheduleConfigComponentRef>();

    return (
        <StyledContainer>
            <div className="title">
                <Header4>{title}</Header4>
                <div className="top-section">
                    <Body2>
                        {`${description} `}
                        <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
                    </Body2>
                    <Button color="primary" variant="contained" disabled={saving || !isValid} onClick={() => configComponent.current.save()}>
                        {saving && <FontAwesomeIcon style={{ marginRight: inlineTextIconMargin }} icon={{ prefix: "fas", iconName: "circle-notch" }} className="fa-spin" />}
                        <span>{translate(Content.settings.scheduleConfig.save)}</span>
                    </Button>
                </div>
            </div>
            <ScheduleConfigComponent
                scheduleConfigService={configService}
                onSaving={(saving) => setSaving(saving)}
                onSuccess={(schedule) => (onSaved ? onSaved(schedule) : undefined)}
                extractNetworkError={(error: AxiosError<string>) => error.response?.data}
                setValidation={(valid) => setIsValidation(valid)}
                ref={configComponent}
            />
        </StyledContainer>
    );
};

const StyledContainer = styled.div`
    flex-grow: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    padding: ${sectionalMargin};
    overflow-y: auto;

    ${media.extraSmall} {
        padding: ${modalPaddingExtraSmall};
        /* Overflow does not show on mobile without this */
        height: 100%;
    }

    .title {
        margin-bottom: ${sectionalMargin};
    }

    .top-section {
        display: flex;
        justify-content: space-between;

        p {
            flex: 1 1;
        }

        button {
            margin-left: 10px;
            height: 40px;
        }

        ${media.extraSmall} {
            flex-direction: column;
            gap: ${formFieldVerticalGap};
        }
    }
`;
